import { useState } from 'react';
import { Link } from 'react-router-dom';

import { DeltaBar, PriceHistory } from '../components/Charts.tsx';
import { DataTable } from '../components/DataTable.tsx';
import { ItemLabel } from '../components/ItemLabel.tsx';
import { Badge, Card, Empty, Notice, Spinner } from '../components/Ui.tsx';
import { api } from '../lib/api.ts';
import { useAsync, useAuth } from '../lib/auth.tsx';
import { moneyShort, number, percent, unitMoney } from '../lib/format.ts';

type Filter = 'all' | 'overpriced' | 'underpriced' | 'competitive';

export function PricingPage() {
  const { can } = useAuth();
  const [filter, setFilter] = useState<Filter>('all');
  const [selected, setSelected] = useState<string | null>(null);

  const mispricing = useAsync(() => api.insights.mispricing(), []);
  const history = useAsync(
    () => (selected ? api.market.history(selected) : Promise.resolve(null)),
    [selected],
  );
  const competitors = useAsync(
    () => (selected ? api.market.competitors(selected) : Promise.resolve(null)),
    [selected],
  );

  if (mispricing.loading) return <Spinner />;
  const rows = mispricing.data?.rows ?? [];
  const filtered = filter === 'all' ? rows : rows.filter((row) => row.verdict === filter);
  const selectedRow = rows.find((row) => row.itemCode === selected) ?? null;

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Price vs market</h1>
          <p>
            Your latest selling price against what shops with stock actually charge, from the
            server-wide ChestShop market. Out-of-stock listings are excluded - an empty chest is not
            competition. Ranked by revenue at risk.
          </p>
        </div>
        <div className="pill-row">
          {(['all', 'overpriced', 'underpriced', 'competitive'] as Filter[]).map((option) => (
            <button
              key={option}
              className={`pill ${filter === option ? 'active' : ''}`}
              onClick={() => setFilter(option)}
            >
              {option}
              {option !== 'all' && ` (${rows.filter((r) => r.verdict === option).length})`}
            </button>
          ))}
        </div>
      </header>

      {mispricing.data?.truncated && (
        <div style={{ marginBottom: 16 }}>
          <Notice>
            <strong>
              Showing {rows.length} of {mispricing.data.totalItems} items.
            </strong>{' '}
            The free teaser caps the table. <Link to="/billing">Upgrade</Link> to benchmark
            everything you sell.
          </Notice>
        </div>
      )}

      <Card
        title={`${filtered.length} item${filtered.length === 1 ? '' : 's'}`}
        subtitle="Click a row to see how the market price for that item has moved"
      >
        <DataTable
          rows={filtered}
          columns={[
            {
              key: 'item',
              header: 'Item',
              searchText: (row) => `${row.displayName} ${row.itemCode}`,
              sortValue: (row) => row.displayName,
              render: (row) => (
                <button
                  className="ghost"
                  style={{
                    padding: 0,
                    textAlign: 'left',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                  onClick={() => setSelected(row.itemCode === selected ? null : row.itemCode)}
                >
                  <ItemLabel
                    itemKey={row.itemCode}
                    material={row.itemCode}
                    displayName={row.displayName}
                    sub={<span className="small muted">{row.itemCode}</span>}
                  />
                </button>
              ),
            },
            {
              key: 'yourPrice',
              header: 'Your price',
              align: 'num',
              sortValue: (row) => row.yourPrice,
              render: (row) => unitMoney(row.yourPrice),
            },
            {
              key: 'benchmark',
              header: 'Market median',
              align: 'num',
              sortValue: (row) => row.benchmark,
              render: (row) => (
                <>
                  {unitMoney(row.benchmark)}
                  <div className="small muted">
                    {row.benchmarkSource === 'market'
                      ? `${number(row.sampleSize)} shops in stock`
                      : row.benchmarkSource === 'find_baseline'
                        ? '/find baseline'
                        : row.benchmarkSource === 'blended'
                          ? 'network + /find'
                          : `${number(row.sampleSize)} of your sales`}
                  </div>
                </>
              ),
            },
            {
              key: 'bar',
              header: 'Under / over',
              render: (row) => <DeltaBar pct={row.deltaPct} />,
            },
            {
              key: 'deltaPct',
              header: 'Gap',
              align: 'num',
              sortValue: (row) => row.deltaPct,
              render: (row) => <strong>{percent(row.deltaPct)}</strong>,
            },
            {
              key: 'band',
              header: 'Suggested band',
              align: 'num',
              sortValue: (row) => row.suggestedLow,
              render: (row) => `${unitMoney(row.suggestedLow)} - ${unitMoney(row.suggestedHigh)}`,
            },
            {
              key: 'confidence',
              header: 'Confidence',
              sortValue: (row) => ({ high: 3, medium: 2, low: 1 })[row.confidence] ?? 0,
              render: (row) => (
                <Badge
                  tone={
                    row.confidence === 'high'
                      ? 'good'
                      : row.confidence === 'medium'
                        ? 'muted'
                        : 'warning'
                  }
                >
                  {row.confidence}
                </Badge>
              ),
            },
            {
              key: 'units',
              header: '30d units',
              align: 'num',
              sortValue: (row) => row.unitsSold30d,
              render: (row) => number(row.unitsSold30d),
            },
            {
              key: 'revenue',
              header: '30d revenue',
              align: 'num',
              sortValue: (row) => row.revenue30d,
              render: (row) => moneyShort(row.revenue30d),
            },
          ]}
          rowKey={(row) => row.itemCode}
          searchPlaceholder="Search items"
          pageSize={40}
          emptyTitle="Nothing matches that filter"
        />
      </Card>

      {selected && (
        <Card
          title={`${selectedRow?.displayName ?? selected} - market price history`}
          subtitle="Daily traded average across every shop on the server, with your own price over the top"
          actions={
            <button className="ghost" onClick={() => setSelected(null)}>
              Close
            </button>
          }
        >
          {history.loading ? (
            <Spinner />
          ) : (history.data?.points.length ?? 0) === 0 ? (
            <Empty title="No network sales recorded for this item yet">
              <span className="muted">
                Its benchmark is coming from the recorded <code>/find</code> baseline alone.
              </span>
            </Empty>
          ) : (
            <PriceHistory points={history.data!.points} />
          )}
        </Card>
      )}

      {selected && (
        <Card
          title={`Who you are competing against for ${selectedRow?.displayName ?? selected}`}
          subtitle="Shops with stock, cheapest first. Your own shops are excluded."
        >
          {competitors.loading ? (
            <Spinner />
          ) : (competitors.data?.competitors.length ?? 0) === 0 ? (
            <Empty title="No competing shop currently has this in stock">
              <span className="muted">
                That is an opportunity - and a reason to check your own price is not the only one
                holding the market up.
              </span>
            </Empty>
          ) : (
            <DataTable
              rows={competitors.data!.competitors}
              rowKey={(competitor) => String(competitor.shopId)}
              initialSort={{ key: 'unitPrice', dir: 'asc' }}
              searchPlaceholder="Search owners or worlds"
              pageSize={15}
              columns={[
                {
                  key: 'owner',
                  header: 'Shop owner',
                  searchText: (c) => c.ownerName ?? '',
                  sortValue: (c) => c.ownerName,
                  render: (c) => (
                    <>
                      {c.ownerName ?? <span className="muted">unnamed</span>}
                      {c.adminShop && <span className="small muted"> (admin)</span>}
                    </>
                  ),
                },
                {
                  key: 'unitPrice',
                  header: 'Unit price',
                  align: 'num',
                  sortValue: (c) => c.unitPrice,
                  render: (c) => unitMoney(c.unitPrice),
                },
                {
                  key: 'vsYou',
                  header: 'vs you',
                  align: 'num',
                  sortValue: (c) =>
                    selectedRow ? (c.unitPrice - selectedRow.yourPrice) / selectedRow.yourPrice : null,
                  render: (c) => {
                    if (!selectedRow) return '-';
                    const delta =
                      ((c.unitPrice - selectedRow.yourPrice) / selectedRow.yourPrice) * 100;
                    return (
                      <span className={delta < 0 ? 'delta-down' : 'delta-up'}>
                        {percent(delta)}
                      </span>
                    );
                  },
                },
                {
                  key: 'stock',
                  header: 'Stock',
                  align: 'num',
                  sortValue: (c) => c.currentStock,
                  render: (c) => number(c.currentStock ?? 0),
                },
                {
                  key: 'where',
                  header: 'Location',
                  searchText: (c) => `${c.world ?? ''} ${c.coords ?? ''}`,
                  sortValue: (c) => c.world,
                  render: (c) => (
                    <span className="muted small">
                      {c.world ?? '-'}
                      {c.coords ? ` (${c.coords})` : ''}
                    </span>
                  ),
                },
              ]}
            />
          )}
        </Card>
      )}

      {!can('benchmarks.full') && (
        <div style={{ marginTop: 16 }}>
          <Notice>
            <strong>Low-confidence benchmarks are shown, not hidden.</strong> An item backed by two
            listings is labelled as such rather than presented as a firm number - a thin benchmark
            that looks confident is worse than no benchmark.
          </Notice>
        </div>
      )}
    </>
  );
}
